import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useAuthStore } from '../auth/useAuthStore';
import { useSessionStore } from '../auth/useSessionStore';
import { isAdminRole, isSystemAdministrator } from '../auth/rbac';
import { PRODUCT_DETAIL_ROUTE } from '../ordering/orderingChoices';
import { RequireOrderingChoice } from '../ordering/RequireOrderingChoice';
import { AdminUsers } from './AdminUsers/AdminUsers';
import { AppRoute, RedirectToSignIn } from './AppRoute';
import { AuditLog } from './AuditLog/AuditLog';
import { CartPage } from './Cart/CartPage';
import { ChoosePage } from './Choose/ChoosePage';
import { HhtOrdersPage } from './HhtOrders/HhtOrdersPage';
import { LandingPage } from './Landing/LandingPage';
import { ImportMonitoring } from './ImportMonitoring/ImportMonitoring';
import { MyAccount } from './MyAccount/MyAccount';
import { OrderHistory } from './Orders/OrderHistory';
import { ProductDetail } from './ProductDetail/ProductDetail';
import { Products } from './Products/Products';

function LoadingRoute({ account = false }: { account?: boolean }) {
  return (
    <main>
      <p role="status">{account ? 'Loading your account…' : 'Loading…'}</p>
    </main>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (status === 'initializing') return <LoadingRoute />;
  if (!(status === 'ready' && isAuthenticated)) return <RedirectToSignIn />;
  return children;
}

/** Story 8.1 (ADO-2010), AC4 / AC5: a product browsing route. Signed in first, then a
 *  store user with no active ordering choice is sent to `/choose` (and back after it). */
function BrowsingRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <RequireOrderingChoice>{children}</RequireOrderingChoice>
    </ProtectedRoute>
  );
}

/**
 * Gate for the admin routes. Display/routing only — every API action behind these
 * routes is independently authorized server-side (Story 6.34, AC2).
 *
 * `roleKnown` exists because `allowed` alone conflates two very different states.
 * `fetchTenantSession` degrades to `null` on ANY failure (network, non-200, parse)
 * and the bootstrap runs once per page load with no retry — so a genuine admin
 * whose tenant-context read blipped arrives here with `role === null`, which every
 * role predicate rejects. Treating that as "denied" redirects them to `/account`
 * with no error and no way back short of a manual reload (code review 2026-07-31).
 * Distinguish the two: an unknown role is a recoverable failure with a retry, a
 * known-but-insufficient role is a real denial that terminates at `/account`.
 */
export function AdminRouteGate({
  allowed,
  roleKnown = true,
  children,
}: {
  allowed: boolean;
  roleKnown?: boolean;
  children: ReactNode;
}) {
  const status = useAuthStore((state) => state.status);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (status === 'initializing') return <LoadingRoute />;
  if (!(status === 'ready' && isAuthenticated)) return <RedirectToSignIn />;
  if (!roleKnown) {
    return (
      <main>
        <p role="alert">
          We couldn&apos;t confirm your permissions. Please check your connection and try again.
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </main>
    );
  }
  if (!allowed) return <Navigate to="/account" replace />;
  return children;
}

function MyAccountRoute() {
  const status = useAuthStore((state) => state.status);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const requestPasswordChange = useAuthStore((state) => state.requestPasswordChange);
  const logout = useAuthStore((state) => state.logout);
  const role = useSessionStore((state) => state.role);
  const accountType = useSessionStore((state) => state.accountType);
  const email = useSessionStore((state) => state.identity?.email ?? null);

  if (status === 'initializing') return <LoadingRoute account />;
  if (!(status === 'ready' && isAuthenticated)) return <RedirectToSignIn />;
  return (
    <MyAccount
      onRequestPasswordChange={requestPasswordChange}
      onSignOut={logout}
      session={{ role, accountType, email }}
    />
  );
}

function AdminUsersRoute() {
  const role = useSessionStore((state) => state.role);
  return (
    <AdminRouteGate allowed={isAdminRole(role)} roleKnown={role !== null}>
      <AdminUsers session={{ role }} />
    </AdminRouteGate>
  );
}

// Story 14.4 (ADO-1090) — /admin/import-monitoring is gated to the same
// user-management admin tier as /admin/users (`isAdminRole`), not to the stricter
// SystemAdministrator-only set that guards the audit log.
function ImportMonitoringRoute() {
  const role = useSessionStore((state) => state.role);
  return (
    <AdminRouteGate allowed={isAdminRole(role)} roleKnown={role !== null}>
      <ImportMonitoring session={{ role }} />
    </AdminRouteGate>
  );
}

function AuditLogRoute() {
  const role = useSessionStore((state) => state.role);
  // Audit log is SystemAdministrator-only (AC5, pending OQ-ADR-020a) — deliberately
  // stricter than the ADMIN_ROLES tier that gates Admin Users above.
  return (
    <AdminRouteGate allowed={isSystemAdministrator(role)} roleKnown={role !== null}>
      <AuditLog session={{ role }} />
    </AdminRouteGate>
  );
}

export function ShopLinkRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AppRoute />} />
      <Route path="/account" element={<MyAccountRoute />} />
      {/* Story 8.1 (ADO-2010): the choice screen (where to order) and the
          Foodservice / Licensed Trade / LSN landing page. Same auth posture as
          /products: both read over the Bearer token, so neither mounts before the
          bootstrap is ready. The browsing routes (landing, product list and detail,
          favourites, cart) are BrowsingRoutes: a store user chooses first (AC4, AC5);
          /account and /orders are not gated. */}
      <Route
        path="/choose"
        element={
          <ProtectedRoute>
            <ChoosePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landing"
        element={
          <BrowsingRoute>
            <LandingPage />
          </BrowsingRoute>
        }
      />
      <Route
        path="/products"
        element={
          <BrowsingRoute>
            <Products />
          </BrowsingRoute>
        }
      />
      {/* Story 7.12 Iteration 2 (ADO-911), DR-1 — the Product Detail Page. Same
          auth posture as /products: the product resolution, availability,
          recommendations and basket calls all carry the Bearer token, so it must
          not mount before the bootstrap is ready (a shared PDP link opened cold
          would otherwise fire those fetches unauthenticated and 401 with no
          retry). Declared AFTER /products so the static path wins over :sku. */}
      <Route
        path={PRODUCT_DETAIL_ROUTE}
        element={
          <BrowsingRoute>
            <ProductDetail />
          </BrowsingRoute>
        }
      />
      <Route path="/browse" element={<Navigate to="/products" replace />} />
      <Route path="/search" element={<Navigate to="/products" replace />} />
      <Route
        path="/favourites"
        element={
          <BrowsingRoute>
            <Products view="favourites" />
          </BrowsingRoute>
        }
      />
      {/* Story 9.4 (ADO-1948): "Previous orders". Same auth posture as /products:
          the list read carries the Bearer token and is tenant + account scoped
          server-side, so it must not mount before the bootstrap is ready. */}
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <OrderHistory />
          </ProtectedRoute>
        }
      />
      {/* Story 8.2 (ADO-2011): the full cart. Same auth posture as /products: the
          basket open and line edits carry the Bearer token and are tenant + depot
          scoped server-side, so it must not mount before the bootstrap is ready. */}
      <Route
        path="/cart"
        element={
          <BrowsingRoute>
            <CartPage />
          </BrowsingRoute>
        }
      />
      {/* Story 8.10 (ADO-2019): the HHT pages (their waiting card sits on /choose, DV-7):
          /hht-orders lists what is waiting (DV-34), /hht-orders/{id} reviews one order and
          shows its added-to-carts result. Same auth posture as /orders: their reads carry
          the Bearer token. No depot is needed: an order is split across the depot carts. */}
      <Route
        path="/hht-orders"
        element={
          <ProtectedRoute>
            <HhtOrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hht-orders/:hhtOrderId"
        element={
          <ProtectedRoute>
            <HhtOrdersPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/users" element={<AdminUsersRoute />} />
      <Route path="/admin/audit-log" element={<AuditLogRoute />} />
      <Route path="/admin/import-monitoring" element={<ImportMonitoringRoute />} />
    </Routes>
  );
}
